import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Select,
  Radio,
  Button,
  Card,
  Typography,
  message,
  InputNumber,
  DatePicker,
  Checkbox,
  Row,
  Col,
  Divider
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import api from '../utils/api';

const { Title, Text } = Typography;
const { Option } = Select;

// 广告目标选项
const objectives = [
  { value: 'outcome_awareness', label: '品牌认知' },
  { value: 'outcome_traffic', label: '流量' },
  { value: 'outcome_engagement', label: '互动' },
  { value: 'outcome_leads', label: '潜在客户' },
  { value: 'outcome_sales', label: '销量' },
  { value: 'outcome_app_promotion', label: '应用推广' },
];

// 出价策略选项
const bidStrategies = [
  { value: 'lowest_cost_without_cap', label: '最低成本无上限' },
  { value: 'cost_cap', label: '成本上限' },
  { value: 'lowest_cost_with_bid_cap', label: '最低成本带出价上限' },
  { value: 'lowest_cost_with_min_roas', label: '最低成本带最低ROAS' },
];

// 特殊广告类别
const specialAdCategories = [
  { value: 'housing', label: '住房' },
  { value: 'employment', label: '就业' },
  { value: 'credit', label: '信贷' },
  { value: 'issues_elections_politics', label: '政治议题/选举' },
];

const CampaignCreate = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [budgetMode, setBudgetMode] = useState('CBO');
  const [budgetType, setBudgetType] = useState('daily');
  const [showBidAmount, setShowBidAmount] = useState(false);

  // 预算模式切换
  const handleBudgetModeChange = (e) => {
    setBudgetMode(e.target.value);
    // 重置预算相关字段
    form.setFieldsValue({
      daily_budget: undefined,
      lifetime_budget: undefined,
    });
  };

  // 预算类型切换
  const handleBudgetTypeChange = (e) => {
    setBudgetType(e.target.value);
    form.setFieldsValue({
      daily_budget: undefined,
      lifetime_budget: undefined,
    });
  };

  // 出价策略切换
  const handleBidStrategyChange = (value) => {
    setShowBidAmount(['cost_cap', 'lowest_cost_with_bid_cap'].includes(value));
  };

  // 提交表单
  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        name: values.name,
        objective: values.objective,
        budget_mode: values.budget_mode,
        status: values.status,
      };

      // CBO 模式添加预算
      if (values.budget_mode === 'CBO') {
        if (budgetType === 'daily' && values.daily_budget) {
          payload.daily_budget = values.daily_budget;
        } else if (budgetType === 'lifetime' && values.lifetime_budget) {
          payload.lifetime_budget = values.lifetime_budget;
        }
      }

      // 出价策略
      if (values.bid_strategy) {
        payload.bid_strategy = values.bid_strategy;
        if (values.bid_amount) {
          payload.bid_amount = values.bid_amount;
        }
      }

      // 排期
      if (values.start_time) {
        payload.start_time = values.start_time.format('YYYY-MM-DDTHH:mm:ss+08:00');
      }
      if (values.stop_time) {
        payload.stop_time = values.stop_time.format('YYYY-MM-DDTHH:mm:ss+08:00');
      }

      // 特殊广告类别
      if (values.special_ad_categories && values.special_ad_categories.length > 0) {
        payload.special_ad_categories = values.special_ad_categories;
      }

      console.log('Creating campaign with payload:', payload);

      const response = await api.post('/campaigns', payload);
      
      message.success('广告系列创建成功');
      navigate('/campaigns');
    } catch (error) {
      console.error('Create campaign failed:', error);
      message.error(error.response?.data?.detail || '创建广告系列失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/campaigns')}
            style={{ marginRight: 16 }}
          >
            返回
          </Button>
          <Title level={2} style={{ display: 'inline', margin: 0 }}>
            创建广告系列
          </Title>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            status: 'paused',
            budget_mode: 'CBO',
          }}
        >
          {/* 基本信息 */}
          <Card title="基本信息" style={{ marginBottom: 24 }}>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  label="广告系列名称"
                  name="name"
                  rules={[{ required: true, message: '请输入广告系列名称' }]}
                >
                  <Input placeholder="请输入广告系列名称" maxLength={394} showCount />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="广告目标"
                  name="objective"
                  rules={[{ required: true, message: '请选择广告目标' }]}
                >
                  <Select placeholder="请选择广告目标">
                    {objectives.map(obj => (
                      <Option key={obj.value} value={obj.value}>{obj.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="状态"
              name="status"
              rules={[{ required: true }]}
            >
              <Radio.Group>
                <Radio value="paused">暂停</Radio>
                <Radio value="active">启用</Radio>
              </Radio.Group>
            </Form.Item>
          </Card>

          {/* 预算模式选择 */}
          <Card title="预算模式" style={{ marginBottom: 24 }}>
            <Form.Item
              name="budget_mode"
              rules={[{ required: true, message: '请选择预算模式' }]}
            >
              <Radio.Group onChange={handleBudgetModeChange} value={budgetMode}>
                <Radio value="CBO">
                  <div style={{ display: 'inline-block', verticalAlign: 'top' }}>
                    <Text strong>CBO - 广告系列预算优化</Text>
                    <br />
                    <Text type="secondary">Facebook 自动优化预算分配到各个广告组</Text>
                  </div>
                </Radio>
                <Radio value="ABO">
                  <div style={{ display: 'inline-block', verticalAlign: 'top' }}>
                    <Text strong>ABO - 广告组预算</Text>
                    <br />
                    <Text type="secondary">手动设置每个广告组的预算</Text>
                  </div>
                </Radio>
              </Radio.Group>
            </Form.Item>

            {/* CBO 模式预算设置 */}
            {budgetMode === 'CBO' && (
              <div style={{ marginTop: 16, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 8 }}>
                <Form.Item label="预算类型">
                  <Radio.Group onChange={handleBudgetTypeChange} value={budgetType}>
                    <Radio value="daily">每日预算</Radio>
                    <Radio value="lifetime">总预算</Radio>
                  </Radio.Group>
                </Form.Item>

                {budgetType === 'daily' ? (
                  <Form.Item
                    label="每日预算"
                    name="daily_budget"
                    rules={[{ required: true, message: '请输入每日预算' }]}
                  >
                    <InputNumber
                      min={1}
                      placeholder="请输入每日预算"
                      addonAfter="元"
                      style={{ width: 200 }}
                    />
                  </Form.Item>
                ) : (
                  <Form.Item
                    label="总预算"
                    name="lifetime_budget"
                    rules={[{ required: true, message: '请输入总预算' }]}
                  >
                    <InputNumber
                      min={1}
                      placeholder="请输入总预算"
                      addonAfter="元"
                      style={{ width: 200 }}
                    />
                  </Form.Item>
                )}
              </div>
            )}

            {/* ABO 模式提示 */}
            {budgetMode === 'ABO' && (
              <div style={{ marginTop: 16, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 8 }}>
                <Text type="secondary">
                  ABO 模式下，预算将在创建广告组时设置
                </Text>
              </div>
            )}
          </Card>

          {/* 出价策略 */}
          <Card title="出价策略" style={{ marginBottom: 24 }}>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item label="出价策略" name="bid_strategy">
                  <Select
                    placeholder="请选择出价策略"
                    onChange={handleBidStrategyChange}
                    allowClear
                  >
                    {bidStrategies.map(strategy => (
                      <Option key={strategy.value} value={strategy.value}>
                        {strategy.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              {showBidAmount && (
                <Col span={12}>
                  <Form.Item
                    label="出价金额"
                    name="bid_amount"
                    rules={[{ required: true, message: '请输入出价金额' }]}
                  >
                    <InputNumber
                      min={0.01}
                      step={0.01}
                      placeholder="请输入出价金额"
                      addonAfter="元"
                      style={{ width: 200 }}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </Card>

          {/* 排期设置 */}
          <Card title="排期设置（可选）" style={{ marginBottom: 24 }}>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item label="开始时间" name="start_time">
                  <DatePicker
                    showTime
                    placeholder="选择开始时间"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="结束时间" name="stop_time">
                  <DatePicker
                    showTime
                    placeholder="选择结束时间"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* 特殊广告类别 */}
          <Card title="特殊广告类别（可选）" style={{ marginBottom: 24 }}>
            <Form.Item name="special_ad_categories">
              <Checkbox.Group options={specialAdCategories} />
            </Form.Item>
          </Card>

          {/* 提交按钮 */}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<SaveOutlined />}
              size="large"
            >
              创建广告系列
            </Button>
            <Button
              style={{ marginLeft: 16 }}
              onClick={() => navigate('/campaigns')}
              size="large"
            >
              取消
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default CampaignCreate;
